import type { ReactNode } from 'react';
import { useHasFeature, useHasAnyFeature, useHasAllFeatures } from '@/hooks/useFeatureAccess';
import { Feature } from '@/types/auth';
import { Lock } from 'lucide-react';

interface FeatureGateProps {
    /** Single feature required for access */
    feature?: Feature;
    /** Multiple features - user needs at least one (OR logic) */
    anyOf?: Feature[];
    /** Multiple features - user needs all (AND logic) */
    allOf?: Feature[];
    /** Content to show when user has access */
    children: ReactNode;
    /** Optional fallback content when user doesn't have access */
    fallback?: ReactNode;
    /** Optional custom message when access is denied */
    deniedMessage?: string;
    /** Show default "Feature Locked" message if no fallback provided */
    showDeniedMessage?: boolean;
}

/**
 * Component that conditionally renders content based on feature access
 *
 * Usage:
 * ```tsx
 * <FeatureGate feature={Feature.COURSES}>
 *   <CoursesList />
 * </FeatureGate>
 *
 * <FeatureGate
 *   feature={Feature.MEAL_PLANNING}
 *   fallback={<UpgradeMessage />}
 * >
 *   <MealPlanner />
 * </FeatureGate>
 * ```
 */
export const FeatureGate = ({
    feature,
    anyOf,
    allOf,
    children,
    fallback,
    deniedMessage = 'This feature is not available in your current plan.',
    showDeniedMessage = false,
}: FeatureGateProps) => {
    // Always call hooks unconditionally (React rules of hooks)
    // Pass a dummy feature when not needed, the hook handles undefined gracefully
    const singleFeatureResult = useHasFeature(feature as Feature);
    const anyFeatureResult = useHasAnyFeature(anyOf ?? []);
    const allFeaturesResult = useHasAllFeatures(allOf ?? []);

    // Determine access based on which props were provided
    const hasSingleFeature = feature ? singleFeatureResult : true;
    const hasAnyFeature = anyOf && anyOf.length > 0 ? anyFeatureResult : true;
    const hasAllFeatures = allOf && allOf.length > 0 ? allFeaturesResult : true;

    const hasAccess = hasSingleFeature && hasAnyFeature && hasAllFeatures;

    // If user has access, render children
    if (hasAccess) {
        return <>{children}</>;
    }

    // If user doesn't have access and fallback is provided
    if (fallback) {
        return <>{fallback}</>;
    }

    // If user doesn't have access and showDeniedMessage is true
    if (showDeniedMessage) {
        return (
            <div className="flex flex-col items-center justify-center p-8 bg-muted rounded-lg border-2 border-border">
                <Lock className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">Feature Locked</h3>
                <p className="text-muted-foreground text-center max-w-md">{deniedMessage}</p>
                <button className="mt-4 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
                    Upgrade Plan
                </button>
            </div>
        );
    }

    // Default: render nothing if no access and no fallback
    return null;
};

/**
 * Inline variant that shows/hides UI elements without layout disruption
 */
export const FeatureToggle = ({
    feature,
    anyOf,
    allOf,
    children,
}: Pick<FeatureGateProps, 'feature' | 'anyOf' | 'allOf' | 'children'>) => {
    // Always call hooks unconditionally (React rules of hooks)
    const singleFeatureResult = useHasFeature(feature as Feature);
    const anyFeatureResult = useHasAnyFeature(anyOf ?? []);
    const allFeaturesResult = useHasAllFeatures(allOf ?? []);

    // Determine access based on which props were provided
    const hasSingleFeature = feature ? singleFeatureResult : true;
    const hasAnyFeature = anyOf && anyOf.length > 0 ? anyFeatureResult : true;
    const hasAllFeatures = allOf && allOf.length > 0 ? allFeaturesResult : true;

    const hasAccess = hasSingleFeature && hasAnyFeature && hasAllFeatures;

    if (!hasAccess) return null;

    return <>{children}</>;
};
